import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, downloadFile, formatApiError, queuedMessage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Plus, Download, Upload, FileSpreadsheet, MoreHorizontal, Table2, Factory, PackageCheck, XCircle, Percent } from "lucide-react";
import { toast, Toaster } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ProductionWizard } from "@/components/dpr/ProductionWizard";
import { ProductionLog } from "@/components/dpr/ProductionLog";
import { DPRFullSheet } from "@/components/dpr/DPRFullSheet";
import { flattenEntries, rejPillClass, todayStr } from "@/components/dpr/dprUtils";

function Kpi({ id, icon: Icon, label, value, accent, pill }) {
  return (
    <div className={`section-card ${accent} flex items-center gap-3 !py-4`} data-testid={id}>
      <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><Icon size={18} /></span>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        {pill ? <span className={`stat-pill mt-1 ${pill}`}>{value}</span> : <div className="font-heading text-2xl font-bold text-slate-900 leading-tight">{value}</div>}
      </div>
    </div>
  );
}

export default function DPR() {
  const { user, isSuper, canWrite } = useAuth();
  const isAdmin = isSuper || user?.role === "admin";
  const isOp = !isAdmin;
  const canEditDpr = canWrite("dpr");
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [links, setLinks] = useState({});
  const [range, setRange] = useState({ start: todayStr(), end: todayStr() });
  const [wizard, setWizard] = useState(null); // null | { initial?: entry }
  const [sheet, setSheet] = useState(null); // null | { form? }
  const fileRef = useRef(null);

  const load = useCallback(() => Promise.all([
    api.get("/dpr", { params: { start: range.start, end: range.end } }), api.get("/producible-items"), api.get("/processes"), api.get("/model-process-links"),
  ]).then(([d, m, p, l]) => { setItems(d.data); setModels(m.data); setProcesses(p.data); setLinks(l.data); }).catch((e) => toast.error(formatApiError(e))), [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const entries = useMemo(() => flattenEntries(items, processes), [items, processes]);
  const todayEntries = useMemo(() => entries.filter((e) => e.date === todayStr()), [entries]);
  const todayCount = useMemo(() => { const m = {}; todayEntries.forEach((e) => { m[e.process_id] = (m[e.process_id] || 0) + 1; }); return m; }, [todayEntries]);
  const stats = useMemo(() => {
    const inQ = entries.reduce((s, e) => s + (e.input_qty || 0), 0);
    const rej = entries.reduce((s, e) => s + (e.rejected_qty || 0), 0);
    return { n: entries.length, inQ, rej, pct: inQ > 0 ? Math.round((rej / inQ) * 1000) / 10 : 0 };
  }, [entries]);
  const multiDay = range.start !== range.end;

  const onBulkUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await api.post("/import/dpr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { inserted_rows, dpr_entries, errors } = res.data;
      if (dpr_entries > 0) toast.success(`Imported ${dpr_entries} day(s) · ${inserted_rows} stage rows.`);
      if (errors?.length) toast.warning(`${errors.length} row(s) skipped. ${errors[0]}`);
      if (!dpr_entries && !errors?.length) toast.info("No rows found to import.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteStage = async (entry, rmk) => {
    try {
      const res = await api.delete(`/dpr/${entry.dpr_id}/stages/${entry.sid}`, isOp ? { params: { remark: rmk } } : {});
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || "Entry removed · stock restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStart = (start) => {
    let end = range.end;
    if (new Date(end) < new Date(start)) end = start;
    if ((new Date(end) - new Date(start)) / 86400000 > 30) { toast.error("Period cannot exceed 30 days."); return; }
    setRange({ start, end });
  };
  const setEnd = (end) => {
    if ((new Date(end) - new Date(range.start)) / 86400000 > 30) { toast.error("Period cannot exceed 30 days."); return; }
    if (new Date(end) < new Date(range.start)) { toast.error("End date must be after start date."); return; }
    setRange({ ...range, end });
  };

  return (
    <Layout title="Production" subtitle="Pick the machine → enter pieces → save. Stock moves to the next stage automatically."
      actions={
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="hidden md:flex items-center gap-1.5 bg-white border border-slate-300 rounded-lg px-2 py-1" data-testid="dpr-period-filter">
              <input data-testid="dpr-period-start" type="date" max={todayStr()} value={range.start} onChange={(e) => setStart(e.target.value)} className="text-sm border-0 outline-none w-32" />
              <span className="text-slate-400 text-xs">→</span>
              <input data-testid="dpr-period-end" type="date" max={todayStr()} value={range.end} onChange={(e) => setEnd(e.target.value)} className="text-sm border-0 outline-none w-32" />
              <button data-testid="dpr-period-today" onClick={() => setRange({ start: todayStr(), end: todayStr() })} className="text-xs font-medium text-blue-600 hover:text-blue-700 px-1.5">Today</button>
            </div>
          )}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onBulkUpload} className="hidden" data-testid="dpr-upload-input" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="dpr-tools-btn" className="bg-white border border-slate-300 hover:border-slate-400 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><MoreHorizontal size={16} /> Tools</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem data-testid="dpr-export-btn" onClick={() => downloadFile(`/export/dpr?start=${range.start}&end=${range.end}`, `DPR_${range.start}_to_${range.end}.xlsx`)}><Download size={15} className="mr-2" /> Export to Excel</DropdownMenuItem>
              <DropdownMenuItem data-testid="dpr-template-btn" onClick={() => downloadFile("/import/dpr/template", "DPR_Upload_Template.xlsx")}><FileSpreadsheet size={15} className="mr-2" /> Download upload template</DropdownMenuItem>
              {canEditDpr && <DropdownMenuItem data-testid="dpr-upload-btn" onClick={() => fileRef.current?.click()}><Upload size={15} className="mr-2" /> Bulk upload (Excel)</DropdownMenuItem>}
              {isAdmin && canEditDpr && <><DropdownMenuSeparator /><DropdownMenuItem data-testid="dpr-advanced-sheet-btn" onClick={() => setSheet({})}><Table2 size={15} className="mr-2" /> Advanced sheet (all stages)</DropdownMenuItem></>}
            </DropdownMenuContent>
          </DropdownMenu>
          {canEditDpr && <button data-testid="dpr-add-btn" onClick={() => setWizard({})} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm"><Plus size={16} /> Record production</button>}
        </div>
      }
    >
      <Toaster richColors position="top-right" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi id="dpr-kpi-entries" icon={Factory} label="Entries" value={stats.n.toLocaleString()} accent="kpi-accent" />
        <Kpi id="dpr-kpi-input" icon={PackageCheck} label="Pieces processed" value={stats.inQ.toLocaleString()} accent="kpi-success" />
        <Kpi id="dpr-kpi-rejected" icon={XCircle} label="Rejected" value={stats.rej.toLocaleString()} accent="kpi-danger" />
        <Kpi id="dpr-kpi-pct" icon={Percent} label="Rejection %" value={`${stats.pct}%`} accent="kpi-warning" pill={rejPillClass(stats.pct)} />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-slate-500" data-testid="dpr-scope-label">
          Showing: <b className="text-slate-700">{!multiDay ? (range.start === todayStr() ? "Today" : range.start) : `${range.start} → ${range.end}`}</b>
          {isOp && <span className="text-xs text-slate-400 ml-2">· your entries for today</span>}
        </div>
        {canEditDpr && entries.length > 0 && <button data-testid="dpr-add-btn-inline" onClick={() => setWizard({})} className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus size={15} /> Add entry</button>}
      </div>

      <ProductionLog entries={entries} showDate={multiDay} isOp={isOp} canEdit={canEditDpr}
        onEdit={(e) => setWizard({ initial: e })} onDeleteStage={deleteStage} />

      {wizard && (
        <ProductionWizard processes={processes} models={models} links={links} todayCount={todayCount} isOp={isOp} canBackdate={isAdmin}
          initial={wizard.initial || null} onClose={() => { setWizard(null); load(); }} onSaved={load} currentUser={user} />
      )}
      {sheet && <DPRFullSheet processes={processes} models={models} links={links} isOp={isOp} initialForm={sheet.form} onClose={() => { setSheet(null); load(); }} onChanged={load} />}
    </Layout>
  );
}
